import type { GameState, VisitLogEntry, Dex, Island } from './state';
import { VISIT_LOG_LIMIT } from './state';
import { totalGardenAttributes } from './garden';
import { customerRates, CUSTOMER_CATALOG, getCustomer } from './customers';
import { specialTipBonus, specialTipMultiplier } from './menu';
import type { MenuState } from './menu';
import { islandSeason } from './season';
import type { Season } from './season';
import { arrivalMultiplier, reputation } from './reputation';
import { nextRandom } from './rng';
import type { Planting } from './plants';
import { stageForGrowSec, type GrowthStage } from './plants';
import { timeOfDayForMs } from './daynight';
import { effectiveSeason } from './season';
import { activeSynergies, growthBoostByCell } from './synergy';
import { isWilted, isWiltingDeco, vitalityAt } from './wilt';
import { ENTRANCE_INDEX } from './garden';
import {
  chooseSeat,
  makeCellContribution,
  ambianceTipMultiplier,
} from './seat';
import { pathThroughputMultiplier } from './path';

// 客 ID → チップの索引（カタログから一度だけ構築）。来店成立時に引く。
const TIP_BY_ID: ReadonlyMap<string, number> = new Map(
  CUSTOMER_CATALOG.map((c) => [c.id, c.tip]),
);

/**
 * 来店抽選の固定刻み（秒）。v0.5.1 で 60→30 に短縮。刻みを短くすることで
 * 「数十秒に 1 体」のテンポを出しやすくし、客がいない無音の時間を減らす。
 * レートは ratePerMin（1 分あたり）なので、1 刻みの確率は ratePerMin×(TICK_SEC/60)。
 */
export const TICK_SEC = 30;

/**
 * 1 刻み（TICK_SEC）あたり 1 客種が来店し得る最大人数（v0.7.0）。
 *
 * v0.5.1 までは「1 刻みに最大 1 人」の単純 Bernoulli（確率 = min(0.9, rate)）だったが、
 * これだと基本客（モコモケ）のように rate が高い客は評判が少し育っただけで確率が上限に
 * 張り付き、**評判倍率も石畳パスのスループット倍率も結果に現れなくなる**（飽和）。
 *
 * v0.7.0 では「期待来店数 = rate」を整数部（確定来店）＋小数部（Bernoulli）に分けて
 * 多重来店を許す。これにより倍率が常に来店数へ線形に効く（飽和しない）。整数部は乱数を
 * 使わず、小数部だけ 1 回だけ PRNG を引く（客種ごとに必ず 1 draw＝既存の乱数列の順序を
 * 保つ）。runaway を防ぐため 1 刻みの人数だけ上限でクランプする（cozy 方針）。
 */
const PER_TICK_MAX_ARRIVALS = 4;

/**
 * 来店シミュレーションの 1 刻みを処理する純関数。
 *
 * 客カタログ順に必ず 1 回ずつ PRNG を引く（出現条件を満たさない客も draw を
 * 消費する）ことで、庭の構成が変わっても乱数列の順序が安定し、決定性が保たれる。
 *
 * @param seed      この刻みの開始 seed。
 * @param attrs     庭属性合計（来店レートの算出に使う）。
 * @param atMs      この刻みの来店記録に刻む時刻。
 * @returns 来た客のリスト（カタログ順）と、次の刻みに渡す seed。
 */
function simulateVisitTick(
  seed: number,
  attrs: ReturnType<typeof totalGardenAttributes>,
  atMs: number,
  tipBonus: number,
  arrivalMult: number,
  env: { tod: ReturnType<typeof timeOfDayForMs>; season: ReturnType<typeof effectiveSeason>; islandSeasons: Season[] },
  seatCtx: SeatTickContext,
  menu: MenuState,
): { visits: VisitLogEntry[]; tip: number; seed: number; dexHits: string[] } {
  const rates = customerRates(attrs, env);
  // その客の好む季節を季節別館がアンロック済みで持っているか（テーマ一致のスペシャル倍率に使う）。
  const islandSeasonSet = new Set<Season>(env.islandSeasons);
  const visits: VisitLogEntry[] = [];
  const dexHits: string[] = [];
  let tipTotal = 0;
  let s = seed;

  // 1 刻みの確率は ratePerMin（1 分あたり）× 刻みの分数 × 評判の来店倍率。
  // 刻みを 60→30 秒に短くしたので tickFrac=0.5。評判で乗算的に賑わう。
  const tickFrac = TICK_SEC / 60;

  // この刻みの間だけ席の占有を進める（来た客がカタログ順に空席を埋める）。
  // 占有は刻みをまたいで持ち越さない（各刻みは独立した抽選＝刻み分割で結果不変を保つ）。
  const occupied = new Set<number>();

  for (const { id, ratePerMin } of rates) {
    // 客種ごとに必ず 1 回だけ PRNG を引く（庭が変わっても乱数列の順序が安定＝決定性）。
    const draw = nextRandom(s);
    s = draw.next;
    if (ratePerMin <= 0) continue;

    // 期待来店数 = rate×刻み分数×評判倍率（＋石畳パス倍率は arrivalMult に内包）。
    // 整数部は確定来店、小数部は draw で 1 回だけ Bernoulli 判定する。これで倍率が
    // 飽和せず線形に効く（評判・石畳パスが来店数に現れる）。runaway は上限でクランプ。
    const expected = Math.max(0, ratePerMin * tickFrac * arrivalMult);
    let arrivals = Math.floor(expected);
    if (draw.value < expected - arrivals) arrivals += 1;
    if (arrivals > PER_TICK_MAX_ARRIVALS) arrivals = PER_TICK_MAX_ARRIVALS;

    for (let n = 0; n < arrivals; n++) {
      // 来店成立。着席先を決定的に選ぶ（空席のうち近接アンビエンス最大、同点は index）。
      // 席が無ければ立ち飲み（アンビエンスボーナス無し）。乱数は使わない＝決定的。
      const seat = chooseSeat(seatCtx.garden, occupied, seatCtx.contribution);
      const ambiance = seat ? seat.ambiance : 0;
      if (seat) occupied.add(seat.seatIndex);

      // チップ: カタログ値 + 本日のスペシャル → 席まわりの近接アンビエンス倍率 → スペシャル乗算。
      // 良いデコで囲んだ席ほど儲かる（席）＋スペシャルを focus 客・季節別館に合わせるほど儲かる（メニュー×島）。
      // すべて決定的（乱数なし）。floor して整数チップにする。
      const cust = getCustomer(id);
      const seasonHosted = !!(cust && cust.favoriteSeason && islandSeasonSet.has(cust.favoriteSeason));
      const spMult = specialTipMultiplier(menu, cust ? cust.keyAttribute : '', seasonHosted);
      const base = tipForCustomer(id) + tipBonus;
      const tip = Math.floor(base * ambianceTipMultiplier(ambiance) * spMult);
      visits.push({ customerId: id, atMs, tip });
      dexHits.push(id);
      tipTotal += tip;
    }
  }
  return { visits, tip: tipTotal, seed: s, dexHits };
}

/** 着席選択・近接積分に渡す刻みごとの文脈（庭と、その刻みの実効寄与関数）。 */
interface SeatTickContext {
  garden: Island['garden'];
  /** セル index → アンビエンス寄与スカラー（level・ゆらぎ反映、makeCellContribution）。 */
  contribution: (cellIndex: number) => number;
}

/** 客 ID → チップ。カタログ索引から引く（未知 ID は 0）。 */
function tipForCustomer(id: string): number {
  return TIP_BY_ID.get(id) ?? 0;
}

/** 図鑑に来店を反映した新しい Dex を返す（入力は破壊しない）。 */
function applyDexHits(dex: Dex, hits: string[], atMs: number): Dex {
  if (hits.length === 0) return dex;
  const next: Dex = { ...dex };
  for (const id of hits) {
    const prev = next[id] ?? { discovered: false, count: 0, firstAtMs: null };
    next[id] = {
      discovered: true,
      count: prev.count + 1,
      firstAtMs: prev.firstAtMs ?? atMs,
    };
  }
  return next;
}

/** 訪問ログに追記し、直近 VISIT_LOG_LIMIT 件に丸めた新配列を返す。 */
function appendVisits(log: VisitLogEntry[], add: VisitLogEntry[]): VisitLogEntry[] {
  if (add.length === 0) return log;
  const merged = log.concat(add);
  return merged.length > VISIT_LOG_LIMIT ? merged.slice(merged.length - VISIT_LOG_LIMIT) : merged;
}

/**
 * 経過時間ぶんゲーム状態を進める決定的な純関数。
 *
 * 同じ入力 (state, elapsedSec) からは必ず同じ出力が得られる（実時刻・グローバル
 * 状態・Math.random に触れない）。乱数が要る来店抽選は state.seed を進める形で
 * 決定的に行う。リアルタイムのフレーム更新にもオフライン精算にも同じ関数を使う。
 *
 * 来店は固定刻み TICK_SEC（60 秒）ごとに抽選する。1 刻みに満たない端数は
 * state.visitAccumSec に溜め、刻みをどう分割しても結果が一致するようにする。
 *
 * @param state    現在のゲーム状態（破壊しない）。
 * @param elapsedSec 経過秒数。負値は 0 に丸める（時計の巻き戻し対策）。
 * @returns 新しい GameState（入力は変更しない）。
 */
export function simulate(state: GameState, elapsedSec: number): GameState {
  const dt = elapsedSec > 0 ? elapsedSec : 0;

  // --- チップ生成（時間で貯まる基礎収入） ---
  let chips = state.chips + state.chipRate * dt;

  // --- 盆栽の成長（実時間で累計成長秒を進める） ---
  // 成長は経過秒を足すだけの連続量だが、v0.6.0 で隣接シナジー（池×若木）の成長ブースト
  // をセルごとに掛ける。ブーストは配置で決まり時間内で一定なので、刻みをどう分割しても
  // 結果が一致し（合成可能）、乱数も使わないので決定的。樹形は植えた時点で固定。
  //
  // v0.7.1: 全島の植物を成長させる（拡張島の庭も育つ）。シナジーの成長ブーストは島内で
  // 評価する（シナジーは島をまたがない）。空配列のコピーを避けるため、変化が無ければ
  // 元の islands を使い回す。
  const islands = growAllIslands(state.islands, dt);

  // --- 来店抽選（固定刻み） ---
  // 属性は成長後の plantings を反映して評価する（長時間オフラインで古木に育った
  // 木が「樹守ねむ」を呼ぶ、等を成立させる）。来店レートは全島合算属性で評価する
  // （拡張島の庭が本店の集客を後押しする＝v0.7.1）。
  //
  // v0.6.0: ゆらぎ（しおれ）・昼夜・季節は時刻で変わるため、属性と来店係数を「刻みの
  // 絶対時刻 atMs」から毎刻み評価する。atMs は state.lastTickMs + 経過で決まるので、
  // 同じ state + 経過からは必ず同じ結果になり、刻みの分割にも依存しない（合成可能）。
  // 庭の構成自体は simulate 内で変わらないので庭デコ ID 集合は不変＝季節固定も一定。
  const baseState: GameState = { ...state, islands };
  // 来店経済はホーム島（index 0）に一本化する（席・パス・近接積分・入口はホーム島のみ）。
  const homeGarden = state.islands[0].garden;
  // 本日のスペシャルのチップボーナス（来店 1 組ごとに乗る小ボーナス）。
  const tipBonus = specialTipBonus(state.menu);
  // 季節固定の判定に使う庭デコ ID 集合（全島合算。simulate 内で不変なので一度だけ作る）。
  const decoIds = collectAllDecoIds(state.islands, state.unlockedIslands);
  // アンロック済みの季節別館（拡張島）の季節（v0.8.1）。その季節の常連を底上げ＋テーマ一致判定に使う。
  // simulate 内で unlockedIslands は不変なので一度だけ作る。
  const islandSeasons: Season[] = [];
  for (let i = 1; i < Math.min(state.unlockedIslands, state.islands.length); i++) {
    const s = islandSeason(i);
    if (s) islandSeasons.push(s);
  }

  // 入口 → カフェの石畳連結によるスループット倍率（ホーム島の配置で決まり不変＝決定的）。
  const pathMult = pathThroughputMultiplier(homeGarden, ENTRANCE_INDEX);

  let seed = state.seed;
  let visitLog = state.visitLog;
  let dex = state.dex;
  let totalVisits = state.totalVisits;

  let budget = state.visitAccumSec + dt;
  // この呼び出し内で消費した「来店処理済み秒」。来店記録の時刻に使う。
  let processedSec = 0;
  while (budget >= TICK_SEC) {
    budget -= TICK_SEC;
    processedSec += TICK_SEC;
    const atMs = state.lastTickMs + processedSec * 1000;
    // この刻みの環境（時間帯・季節）と、ゆらぎ込みの庭属性を atMs から評価する。
    const tod = timeOfDayForMs(atMs);
    const season = effectiveSeason(atMs, decoIds);
    // 来店レートの属性は全島合算（拡張島の庭も集客に効く、v0.7.1）。
    const attrs = totalGardenAttributes(baseState, atMs);
    // 評判による来店倍率は「この刻み開始時点の状態（更新中の totalVisits・dex）」から
    // 毎刻み導く。totalVisits・dex は刻み境界で stepped/once 一致するので、倍率も一致し、
    // 刻みをどう分割しても結果が変わらない（合成可能性を保つ）。
    const arrivalMult = arrivalMultiplier(reputation({ ...state, totalVisits, dex })) * pathMult;
    // 席まわりの近接アンビエンス積分に使う「セル→寄与」関数（ホーム島で評価）。ゆらぎ
    // （しおれ）は atMs で評価して反映する（しおれた飾りの席は魅力が下がる）。
    const contribution = makeCellContribution(homeGarden, (i: number) => {
      const c = homeGarden[i];
      if (!c || !isWiltingDeco(c.decoId)) return 1.0;
      return vitalityAt(c.decoId, i, state.seed, atMs);
    });
    const tick = simulateVisitTick(
      seed,
      attrs,
      atMs,
      tipBonus,
      arrivalMult,
      { tod, season, islandSeasons },
      { garden: homeGarden, contribution },
      state.menu,
    );
    seed = tick.seed;
    if (tick.visits.length > 0) {
      chips += tick.tip;
      visitLog = appendVisits(visitLog, tick.visits);
      dex = applyDexHits(dex, tick.dexHits, atMs);
      totalVisits += tick.visits.length; // 評判の素（累計来店数）
    }
  }

  return {
    ...state,
    chips,
    seed,
    visitLog,
    dex,
    visitAccumSec: budget,
    islands,
    totalVisits,
  };
}

/** 全島の植物を dt ぶん成長させた新しい islands を返す（島内シナジーの成長ブースト込み）。 */
function growAllIslands(islands: Island[], dt: number): Island[] {
  if (dt <= 0) return islands;
  let changed = false;
  const next = islands.map((island) => {
    const synergies = activeSynergies({ garden: island.garden, plantings: island.plantings });
    const growthBoost = growthBoostByCell(synergies);
    const grown = growPlantings(island.plantings, dt, growthBoost);
    if (grown === island.plantings) return island; // 植物なし＝変化なし
    changed = true;
    return { garden: island.garden, plantings: grown };
  });
  return changed ? next : islands;
}

/** 全アンロック島に置かれているデコ ID の集合（季節固定の判定に使う、v0.7.1）。 */
function collectAllDecoIds(islands: Island[], unlockedIslands: number): string[] {
  const out: string[] = [];
  const n = Math.min(unlockedIslands, islands.length);
  for (let i = 0; i < n; i++) {
    for (const cell of islands[i].garden) if (cell) out.push(cell.decoId);
  }
  return out;
}

/**
 * 全植物の累計成長秒を dt ぶん進めた新しい plantings を返す（入力は破壊しない）。
 * 成長は連続量で乱数を使わないため決定的かつ合成可能。dt が 0 / 植物が無いときは
 * 入力をそのまま返して無駄なコピーを避ける。
 *
 * v0.6.0: 隣接シナジー（池×若木）でセルに成長ブースト倍率がかかっていれば、その
 * セルの植物だけ dt を倍率ぶん多く進める（早く育つ）。ブーストは時間内一定なので合成可能。
 *
 * @param growthBoost セル index（文字列キー）→ 成長倍率。空 Map なら全 1.0 扱い。
 */
function growPlantings(
  plantings: Record<string, Planting>,
  dt: number,
  growthBoost?: Map<number, number>,
): Record<string, Planting> {
  if (dt <= 0) return plantings;
  const keys = Object.keys(plantings);
  if (keys.length === 0) return plantings;
  const next: Record<string, Planting> = {};
  for (const key of keys) {
    const p = plantings[key];
    const mult = growthBoost?.get(Number(key)) ?? 1.0;
    next[key] = { ...p, growSec: p.growSec + dt * mult };
  }
  return next;
}

/** 留守中に植物が段階を進めたイベント（「若木が成木になりました」演出の素）。 */
export interface GrowthEvent {
  /** 庭セル index（cellIndex の値）。 */
  cellIndex: number;
  /** 何の植物デコか（樹守の木かどうか等の演出に使える）。 */
  decoId: string;
  /** 留守前の段階。 */
  from: GrowthStage;
  /** 留守後の段階。 */
  to: GrowthStage;
}

/** settleOffline が返すオフライン来店サマリ（「るすの間に来た客」演出の土台）。 */
export interface OfflineSummary {
  /** 精算に使った経過秒数。 */
  elapsedSec: number;
  /** 留守中に増えたチップ（基礎収入 + チップ合計）。 */
  chipsGained: number;
  /** 客 ID → 留守中の来店回数（0 件は含めない）。 */
  visitsByCustomer: Record<string, number>;
  /** 留守中の総来店数。 */
  totalVisits: number;
  /** この精算で初めて発見した客 ID（初訪問演出用）。 */
  newlyDiscovered: string[];
  /** 留守中に成長段階が上がった植物のイベント（段階が変わったものだけ）。 */
  growthEvents: GrowthEvent[];
  /**
   * 精算時点でしおれているゆらぎデコ（光るキノコ等）があるか（v0.6.0）。
   * るすの間サマリで「キノコがしおれている…」と触れるための軽いフラグ。
   */
  hasWiltedNow: boolean;
}

/**
 * 実時刻に基づくオフライン精算。`lastTickMs` から `nowMs` までの経過を
 * simulate に流し込み、`lastTickMs` を更新する。あわせて「るすの間に来た客」の
 * サマリ（種別ごとの来店数・増えたチップ・新発見）を返す。
 *
 * @param state ロード直後の状態。
 * @param nowMs 現在時刻（エポックミリ秒）。
 * @returns 精算後の状態と、演出用サマリ。
 */
export function settleOffline(
  state: GameState,
  nowMs: number,
): { state: GameState; summary: OfflineSummary } {
  const elapsedSec = Math.max(0, (nowMs - state.lastTickMs) / 1000);

  // 精算前後の chips・図鑑カウントを控える。来店数は dex.count の差分で正確に
  // 数える（visitLog は上限で丸められるため件数の根拠に使わない）。
  const beforeChips = state.chips;
  const beforeCount: Record<string, number> = {};
  const beforeDiscovered = new Set<string>();
  for (const [id, e] of Object.entries(state.dex)) {
    beforeCount[id] = e.count;
    if (e.discovered) beforeDiscovered.add(id);
  }

  // 精算前の各植物の成長段階を控える（段階が変わったものをイベント化する）。
  // v0.7.1: 段階イベント・成長キラッ演出は表示中の島でのみ意味を持つので、ホーム島
  // （index 0）の植物だけを対象にする（拡張島の成長も sim では進むが演出はホーム島基準）。
  const homePlantingsBefore = state.islands[0].plantings;
  const beforeStage: Record<string, GrowthStage> = {};
  for (const [key, p] of Object.entries(homePlantingsBefore)) {
    beforeStage[key] = stageForGrowSec(p.growSec);
  }

  const next = simulate(state, elapsedSec);

  // 留守中に段階が上がった植物を拾う（芽→若木 等）。ホーム島基準。
  const homePlantingsAfter = next.islands[0].plantings;
  const homeGardenAfter = next.islands[0].garden;
  const growthEvents: GrowthEvent[] = [];
  for (const [key, p] of Object.entries(homePlantingsAfter)) {
    const from = beforeStage[key];
    if (from == null) continue; // 留守中に新規に植わることは無いが念のため
    const to = stageForGrowSec(p.growSec);
    if (to !== from) {
      const idx = Number(key);
      const cell = homeGardenAfter[idx];
      growthEvents.push({ cellIndex: idx, decoId: cell ? cell.decoId : '', from, to });
    }
  }

  // 留守中の来店を種別ごとに集計（dex カウントの増分 = 正確な来店数）。
  const visitsByCustomer: Record<string, number> = {};
  let totalVisits = 0;
  for (const [id, e] of Object.entries(next.dex)) {
    const delta = e.count - (beforeCount[id] ?? 0);
    if (delta > 0) {
      visitsByCustomer[id] = delta;
      totalVisits += delta;
    }
  }

  const newlyDiscovered = Object.entries(next.dex)
    .filter(([id, e]) => e.discovered && !beforeDiscovered.has(id))
    .map(([id]) => id);

  // 精算時点でしおれているゆらぎデコがあるか（るすの間サマリの一言用）。全島を見る。
  let hasWiltedNow = false;
  outer: for (let i = 0; i < Math.min(next.unlockedIslands, next.islands.length); i++) {
    const grid = next.islands[i].garden;
    for (let idx = 0; idx < grid.length; idx++) {
      const cell = grid[idx];
      if (cell && isWilted(cell.decoId, idx, next.seed, nowMs)) {
        hasWiltedNow = true;
        break outer;
      }
    }
  }

  const summary: OfflineSummary = {
    elapsedSec,
    chipsGained: next.chips - beforeChips,
    visitsByCustomer,
    totalVisits,
    newlyDiscovered,
    growthEvents,
    hasWiltedNow,
  };

  return {
    state: { ...next, lastTickMs: nowMs },
    summary,
  };
}
